import { useCallback, useState } from 'react';
import { useCatalogService } from '../services/catalogService';
import { useSnackbar, type SnackbarState } from '../../../shared/hooks/useSnackbar';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';
import type { Deployment } from '../../../types/backend';

interface UseDeploymentRowActionsOptions {
  customerId: string;
  deployment: Deployment;
  onRefresh: () => void;
}

export interface DeploymentRowActions {
  submitting: boolean;
  approve: () => Promise<void>;
  acceptActual: () => Promise<void>;
  acknowledge: () => Promise<void>;
  pushDesired: (state: Record<string, unknown>) => Promise<void>;
  terminate: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  requestHeartbeat: () => Promise<void>;
  triggerReconciler: () => Promise<void>;
  resolveRescue: () => Promise<void>;
  snackbar: SnackbarState;
  closeSnackbar: () => void;
}

export function useDeploymentRowActions({
  customerId,
  deployment,
  onRefresh,
}: UseDeploymentRowActionsOptions): DeploymentRowActions {
  const catalogService = useCatalogService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const run = useCallback(
    async <T,>(action: () => Promise<T>, successMsg: string, failMsg: string) => {
      setSubmitting(true);
      try {
        await action();
        showSnackbar(successMsg);
        onRefresh();
      } catch (err) {
        showSnackbar(extractErrorMessage(err, failMsg), 'error');
      } finally {
        setSubmitting(false);
      }
    },
    [onRefresh, showSnackbar],
  );

  return {
    submitting,
    approve: useCallback(
      () =>
        run(
          () => catalogService.approveDeployment(customerId, deployment.id),
          'Deployment approved',
          'Failed to approve deployment',
        ),
      [catalogService, customerId, deployment.id, run],
    ),
    acceptActual: useCallback(
      () =>
        run(
          () => catalogService.acceptActualState(customerId, deployment.id),
          'Actual state accepted',
          'Failed to accept actual state',
        ),
      [catalogService, customerId, deployment.id, run],
    ),
    acknowledge: useCallback(
      () =>
        run(
          () => catalogService.acknowledgeDeployment(customerId, deployment.id),
          'Deployment acknowledged',
          'Failed to acknowledge deployment',
        ),
      [catalogService, customerId, deployment.id, run],
    ),
    pushDesired: useCallback(
      (state: Record<string, unknown>) =>
        run(
          () => catalogService.setDesiredState(customerId, deployment.id, state),
          'Desired state updated',
          'Failed to update desired state',
        ),
      [catalogService, customerId, deployment.id, run],
    ),
    // Terminate = SetDesiredState({ desired_status: 'terminated' }) per the backend contract.
    // DeleteDeployment is admin-only and NOT used from the user-facing modal.
    terminate: useCallback(
      () =>
        run(
          () =>
            catalogService.setDesiredState(customerId, deployment.id, {
              desired_status: 'terminated',
            }),
          'Termination requested — reconciler will tear down the stack',
          'Failed to request termination',
        ),
      [catalogService, customerId, deployment.id, run],
    ),
    // Unsubscribe removes the customer's subscription to the service. Reconciler tears down all
    // deployments matching service+account+region.
    unsubscribe: useCallback(
      () =>
        run(
          () =>
            catalogService.unsubscribe({
              customer_id: customerId,
              services: [deployment.service],
              account_id: deployment.account_id,
              region: deployment.region,
            }),
          'Unsubscribed — reconciler will tear down the service',
          'Failed to unsubscribe',
        ),
      [catalogService, customerId, deployment.account_id, deployment.region, deployment.service, run],
    ),
    requestHeartbeat: useCallback(
      () =>
        run(
          () =>
            catalogService.requestHeartbeat(
              customerId,
              deployment.account_id,
              deployment.region,
            ),
          'Heartbeat requested',
          'Failed to request heartbeat',
        ),
      [catalogService, customerId, deployment.account_id, deployment.region, run],
    ),
    triggerReconciler: useCallback(
      () =>
        run(
          () => catalogService.triggerReconciler('deployment'),
          'Reconciler triggered',
          'Failed to trigger reconciler',
        ),
      [catalogService, run],
    ),
    resolveRescue: useCallback(
      () =>
        run(
          () => catalogService.resolveRescue(customerId, deployment.id),
          'Marked resolved — reconciler picks up on next tick',
          'Failed to resolve rescue',
        ),
      [catalogService, customerId, deployment.id, run],
    ),
    snackbar,
    closeSnackbar,
  };
}
